import { API_BASE_URL, authHeaders } from './config';

export interface KanbanColumn {
  id: string;
  sharedCartId: string;
  title: string;
  description: string;
  color: string;
  position: number;
  maxItems?: number;
  isSystem: boolean;
  settings?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface ProductPlacement {
  id: string;
  sharedCartId: string;
  columnId: string;
  itemId: string;
  position: number;
  placedByUserId?: string;
  placedByGuestId?: string;
  placedAt: string;
  updatedAt: string;
}

export interface KanbanBoard {
  columns: KanbanColumnWithItems[];
  updatedAt: string;
}

export interface KanbanColumnWithItems extends KanbanColumn {
  items: any[]; // SharedCartItem[]
}

export interface CreateColumnRequest {
  title: string;
  description: string;
  color?: string;
  position: number;
  maxItems?: number;
  settings?: Record<string, any>;
}

export interface UpdateColumnRequest {
  title: string;
  description: string;
  color: string;
  position: number;
  maxItems?: number;
  settings?: Record<string, any>;
}

export interface MoveItemRequest {
  itemId: string;
  columnId: string;
  position: number;
  isGuest?: boolean;
}

class KanbanService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/shared-carts`;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  // Get complete kanban board
  async getKanbanBoard(cartId: string): Promise<KanbanBoard> {
    return this.request<KanbanBoard>(`/${cartId}/kanban`);
  }

  // Get all columns for a cart
  async getColumns(cartId: string): Promise<KanbanColumn[]> {
    return this.request<KanbanColumn[]>(`/${cartId}/kanban/columns`);
  }

  // Create a new column
  async createColumn(cartId: string, column: CreateColumnRequest): Promise<KanbanColumn> {
    return this.request<KanbanColumn>(`/${cartId}/kanban/columns`, {
      method: 'POST',
      body: JSON.stringify(column),
    });
  }

  // Update an existing column
  async updateColumn(cartId: string, columnId: string, column: UpdateColumnRequest): Promise<KanbanColumn> {
    return this.request<KanbanColumn>(`/${cartId}/kanban/columns/${columnId}`, {
      method: 'PUT',
      body: JSON.stringify(column),
    });
  }

  // Delete a column
  async deleteColumn(cartId: string, columnId: string): Promise<void> {
    await this.request(`/${cartId}/kanban/columns/${columnId}`, {
      method: 'DELETE',
    });
  }

  // Move item to column
  async moveItemToColumn(cartId: string, moveRequest: MoveItemRequest): Promise<ProductPlacement> {
    return this.request<ProductPlacement>(`/${cartId}/kanban/placements`, {
      method: 'POST',
      body: JSON.stringify(moveRequest),
    });
  }

  // Update item position within column
  async updateItemPosition(cartId: string, placementId: string, position: number): Promise<ProductPlacement> {
    return this.request<ProductPlacement>(`/${cartId}/kanban/placements/${placementId}`, {
      method: 'PUT',
      body: JSON.stringify({ position }),
    });
  }

  // Remove item from board
  async removeItemFromBoard(cartId: string, placementId: string): Promise<void> {
    await this.request(`/${cartId}/kanban/placements/${placementId}`, {
      method: 'DELETE',
    });
  }

  // Get placements for a specific column
  async getColumnPlacements(cartId: string, columnId: string): Promise<ProductPlacement[]> {
    return this.request<ProductPlacement[]>(`/${cartId}/kanban/columns/${columnId}/placements`);
  }
}

export const kanbanService = new KanbanService();